/**
 * Dashboard — `docs/SCREENS.md` §2.
 *
 * Two numbers on this screen are load-bearing and are treated differently from the rest:
 *
 *   **Agents with no accountable owner** is shown even when it is zero, in the same place, every
 *   day. A KPI that appears only when it is bad teaches its readers that its absence means nothing
 *   was measured.
 *
 *   **Short-lived agent credentials** shows a dash, not 100%, when there are no agent credentials
 *   at all. An empty estate is not perfect compliance, and a green 100% over zero rows is the most
 *   flattering possible way to display "we have not started".
 */

import { Link } from "react-router-dom";
import type { IdentityKind } from "@hub/core/browser";
import { relative, useApi } from "../lib/api";
import { Card, CountPill, KindBadge, KpiCard, ListItem, NotLiveBanner, Pill } from "../components";

interface Shape {
  canAct: { humans: number; agents: number };
  needsDecision: { approvals: number; anomalies: number; orphanedAgents: number };
  agentCredentials: { total: number; shortLived: number; shortLivedPct: number | null };
  riskPaths: number;
  pending: {
    id: string; action: string; resourceRef: string; reviewFlag: boolean; createdAt: string;
    subject: { displayName: string; kind: IdentityKind };
  }[];
}

interface Integrations {
  integrations: { port: string; adapter: string; live: boolean }[];
}

export function Dashboard() {
  const { data, error, loading } = useApi<Shape>("/v1/dashboard");
  const integrations = useApi<Integrations>("/v1/integrations");
  const mocks = (integrations.data?.integrations ?? []).filter((i) => !i.live);

  if (loading) return <p className="empty">Loading…</p>;
  if (error) return <p className="err">{error.message}{error.hint ? ` — ${error.hint}` : ""}</p>;
  if (!data) return null;

  const total = data.canAct.humans + data.canAct.agents;
  const decisions = data.needsDecision.approvals + data.needsDecision.anomalies + data.needsDecision.orphanedAgents;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Good morning.</h1>
          <p className="sub">
            {total.toLocaleString()} identities can act on your systems — {data.canAct.humans.toLocaleString()}{" "}
            people and {data.canAct.agents.toLocaleString()} agents.{" "}
            {decisions === 0
              ? "Nothing is waiting on you."
              : `${decisions} item${decisions === 1 ? "" : "s"} need${decisions === 1 ? "s" : ""} a decision.`}
          </p>
        </div>
      </div>

      <NotLiveBanner adapters={mocks} />

      <div className="grid k4" style={{ marginBottom: 14 }}>
        <KpiCard
          label="Identities that can act"
          value={total.toLocaleString()}
          dual={{ human: data.canAct.humans.toLocaleString(), agent: data.canAct.agents.toLocaleString() }}
        />
        <KpiCard
          label="Agent credentials short-lived"
          value={data.agentCredentials.shortLivedPct === null ? "—" : `${data.agentCredentials.shortLivedPct}%`}
          {...(data.agentCredentials.total > 0
            ? { split: { ok: data.agentCredentials.shortLived / data.agentCredentials.total } }
            : {})}
          delta={
            data.agentCredentials.shortLivedPct === null
              ? "No agent credentials yet. That is not the same as full compliance."
              : `${data.agentCredentials.shortLived} of ${data.agentCredentials.total} · target is above 95%`
          }
        />
        <KpiCard
          label="Standing grants onto sensitive data"
          value={data.riskPaths}
          delta="People and agents counted together — a standing human grant is no less a risk path."
        />
        <KpiCard
          label="Agents with no accountable owner"
          value={<CountPill n={data.needsDecision.orphanedAgents} />}
          delta="Held by a database constraint and the leaver process, not by a nightly report."
        />
      </div>

      <div className="grid k2">
        <Card title="Needs your decision" link={<Link to="/access">Access management</Link>}>
          {data.pending.length === 0 && data.needsDecision.anomalies === 0 && (
            <p className="empty">Nothing outstanding.</p>
          )}
          <div className="stack">
            {data.pending.slice(0, 6).map((p) => (
              <ListItem
                key={p.id}
                title={
                  <span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <KindBadge kind={p.subject.kind} label={p.subject.displayName} />
                    <span style={{ color: "var(--muted)" }}>{p.action}</span>
                    {p.reviewFlag && <Pill tone="warn">outside its tier</Pill>}
                  </span>
                }
                detail={<>{p.resourceRef} · {relative(p.createdAt)}</>}
              >
                <Link className="btn small" to="/access">Review</Link>
              </ListItem>
            ))}
            {data.needsDecision.anomalies > 0 && (
              <ListItem
                title={`${data.needsDecision.anomalies} open runtime anomal${data.needsDecision.anomalies === 1 ? "y" : "ies"}`}
                detail="Runtime monitoring is phase 3. The events are recorded; the triage screen is not built."
              >
                <Pill tone="warn">phase 3</Pill>
              </ListItem>
            )}
          </div>
        </Card>

        <Card title="Coverage" link={<Link to="/">Capabilities</Link>}>
          <p className="sub" style={{ marginTop: 0 }}>
            This build covers phases 0 to 2: identity, credentialing and visibility. What is
            specified and not built is named rather than hidden — access reviews, the onboarding
            wizard, the policy editor and alert triage are phases 3 and 4.
          </p>
          <div className="stack" style={{ marginTop: 12 }}>
            <ListItem title="Access reviews" detail="Cadences and scopes are implemented; the interface is phase 4.">
              <Pill tone="dim">phase 4</Pill>
            </ListItem>
            <ListItem title="Runtime monitoring" detail="Anomaly events exist; the fast-path monitor is phase 3.">
              <Pill tone="dim">phase 3</Pill>
            </ListItem>
            <ListItem title="Evidence export" detail="The chain verifies today; the framework-mapped bundle is phase 4.">
              <Pill tone="dim">phase 4</Pill>
            </ListItem>
          </div>
        </Card>
      </div>

      <div style={{ marginTop: 14 }}>
        <Card title="Access graph" link={<Link to="/graph">Open the graph</Link>}>
          <p className="sub" style={{ marginTop: 0 }}>
            {data.riskPaths === 0
              ? "No standing grants reach sensitive or financial data."
              : `${data.riskPaths} standing grant${data.riskPaths === 1 ? "" : "s"} reach sensitive or financial data. Each one is an exception that should be justified or removed.`}
          </p>
        </Card>
      </div>
    </>
  );
}
